import * as pdfjs from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export type PDFDocumentProxy = pdfjs.PDFDocumentProxy;
export type PDFPageProxy = pdfjs.PDFPageProxy;
export type PageViewport = pdfjs.PageViewport;

/**
 * Opens a plan with HTTP range requests: only the pages being looked at are
 * fetched, so a 300-sheet set opens as fast as a 3-sheet one.
 */
export function openPdf(url: string, data?: Uint8Array): pdfjs.PDFDocumentLoadingTask {
  return pdfjs.getDocument({
    ...(data ? { data } : { url }),
    withCredentials: true,
    disableAutoFetch: true,
    disableStream: true,
    rangeChunkSize: 256 * 1024,
    cMapUrl: "/pdfjs/cmaps/",
    cMapPacked: true,
    standardFontDataUrl: "/pdfjs/standard_fonts/",
    isEvalSupported: false,
  });
}

/** Largest canvas we'll allocate on desktop (browsers cap around 16k px per side / ~268 MP). */
export const MAX_CANVAS_PIXELS = 48_000_000;

/**
 * iPadOS/iOS Safari refuses canvases over ~16.7 MP (they silently draw blank) and kills tabs that hold
 * too much canvas memory, so touch devices get a much smaller budget.
 */
export function isTouchDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return (navigator.maxTouchPoints ?? 0) > 0 || /iPad|iPhone|iPod/.test(navigator.userAgent);
}
export function canvasBudget(): { maxPixels: number; maxSide: number } {
  return isTouchDevice() ? { maxPixels: 16_000_000, maxSide: 4096 } : { maxPixels: MAX_CANVAS_PIXELS, maxSide: 16_384 };
}
/**
 * Backing-store scale for a CSS-size w x h canvas: the device pixel ratio, lowered so the canvas stays inside
 * the budget. Above the cap the bitmap is lower-res and the browser stretches it to the CSS size.
 */
export function canvasScale(w: number, h: number, dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1): number {
  const { maxPixels, maxSide } = canvasBudget();
  let out = dpr;
  if (w * h * out * out > maxPixels) out = Math.sqrt(maxPixels / (w * h));
  if (Math.max(w, h) * out > maxSide) out = maxSide / Math.max(w, h);
  return Math.max(out, 1e-3);
}
/** Drops a canvas's backing store right away instead of waiting for GC (matters on iOS). */
export function freeCanvas(c: HTMLCanvasElement): void { c.width = 0; c.height = 0; }

/** Extra canvas pixels allowed for the sharp-zoom overlay (on top of the capped full-page image). */
export function sharpBudget(): number { return isTouchDevice() ? 6_000_000 : 16_000_000; }
